import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import CardMedia from "@mui/material/CardMedia";
import { zIndexMap } from "@/ChnlListingPageV2/utils/zIndexMap";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { IChnlLineupThemeUI } from "@/ChnlListingPageV2/server/themeConfig";
import { focusVisibleOutline } from "@/ChnlListingPageV2/utils/focusVisibleOutline";
import { MemoizedAddOnPromoLegend as AddOnPromoLegend } from "./AddOnPromoLegend";
import { MemoizedMyFreeInfoBlock as MyFreeInfoBlock } from "./MyFreeInfoBlock";

const bps = DtvTheme.breakpoints;

// [WAT-11256 nit] Hoisted so this object literal isn't re-created every render —
// AddOnPromoLegend is a React.memo, and a fresh inline `margin` object defeated that
// memoization on every CategoryHeader render regardless of whether anything changed.
const ADD_ON_LEGEND_MARGIN = {
  xs: "6px 0 0 0",
  sm: "12px 0 0 0",
  md: "12px 0 0 0"
};

interface CategoryHeaderProps {
  index: number;
  id: string;
  ariaControls: string;
  isAddOnCtgyHead: boolean;
  isMyFreeDtvCtgyHead: boolean;
  label: string;
  expanded: boolean;
  handleClick: (index: number) => void;
  headerHeight?: number;
  addOnPromoLogoInfo: {
    included: string;
    addon: string;
    promo: string;
  };
  theming?: IChnlLineupThemeUI["packagesTab"]["sectionHeaders"];
  myFreeConfig?: any;
  myfreeBgColor?: any;
  isSatelliteChlup?: boolean;
};

export const CategoryExpandIcon = ({ accOpen, iconColor, alt = "Click to toggle Category section" }) => (
  <CardMedia
    alt={alt}
    component="img"
    src="https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg"
    style={{
      height: "16px",
      width: "16px",
      ...accOpen && { transform: "rotate(180deg)" }
    }}
    sx={{
      [bps.up(bps.values.sm)]: {
        height: "20px",
        width: "20px"
      },
      color: iconColor,
      filter: "invert(100%) sepia(0%) saturate(7500%) hue-rotate(351deg) brightness(106%) contrast(104%)",
      position: "relative",
      cursor: "pointer",
      margin: 0,
      transition: "transform 200ms cubic-bezier(0.42, 0, 0.58, 1) 150ms"
    }}
  />
);

const CategoryHeader = ({
  index,
  id,
  ariaControls,
  isAddOnCtgyHead,
  isMyFreeDtvCtgyHead,
  label,
  expanded,
  handleClick,
  headerHeight,
  addOnPromoLogoInfo,
  theming,
  myFreeConfig,
  myfreeBgColor,
  isSatelliteChlup
}: CategoryHeaderProps): any => {
  // [WAT-12043 / YFH-AMP111] Heading name is set via `aria-labelledby`
  // pointing at the label span below. Rationale for THIS pattern (vs.
  // `aria-label` on the h2, vs. relying on text content):
  //   * `aria-label` — stripped from every h1–h6 by a global idle effect
  //     in `pages/index.tsx` that enforces heading-name-from-text.
  //     `aria-labelledby` isn't touched by that pass.
  //   * Text-content name — folds in AddOnPromoLegend + MyFreeInfoBlock
  //     text rendered inside the summary when the section is expanded,
  //     which is not the semantic name of the heading.
  const labelId = `${id}-heading-label`;
  return (
  // AccordionSummary renders as `<div role="button">`. Heading semantics
  // inside a button role are flattened out of the a11y tree, so the h2
  // must WRAP the summary (APG accordion-in-heading pattern). MUI
  // Accordion reads expanded/disabled state from AccordionContext, not
  // from cloneElement on its first child, so the wrap doesn't disrupt
  // the pattern.
  //
  // The h2 also carries `position: sticky` — putting sticky on the
  // AccordionSummary (a fixed 48 px child) gave it zero scroll range
  // to stick within. The h2 wraps the whole Accordion body, so sticky
  // has the Accordion's expanded height as its containing block.
  //
  // Plain <h2> (not `DtvBox component="h2"`) because the
  // pages/index.tsx sanitizer targets `aria-label` on h1-h6 by walking
  // `document.querySelectorAll('h1, h2, h3, h4, h5, h6')`; wrapping the
  // heading in DtvBox added no protection since the sanitizer sees the
  // rendered element, not the JSX wrapper. Plain h2 keeps the surface
  // as small as possible.
  <h2
    aria-labelledby={labelId}
    style={{
      position: "sticky",
      top: headerHeight,
      left: 0,
      margin: 0,
      padding: 0,
      font: "inherit",
      zIndex: zIndexMap.AccordionSummary
    }}>
    <AccordionSummary
      aria-controls={ariaControls}
      id={id}
      sx={{
        minHeight: "fit-content !important",
        // minHeight : "48px !important",
        // maxHeight: "48px !important", // Override default MUI value
        height: "48px !important",
        backgroundColor: theming?.background,
        color: theming?.textColor,
        borderBottom: `1px solid ${theming?.border}`,
        cursor: "default",
        display: "flex",
        flexDirection: "column",
        // [WAT-12043 / YFH-AMP108] Keyboard focus lands on the AccordionSummary
        // root (a ButtonBase). The previous rule was scoped to
        // `.MuiAccordionSummary-content` (a child div that never receives focus)
        // AND had a stray space in `&: focus`, so nothing rendered.
        ...focusVisibleOutline("&.Mui-focusVisible", "-2px"),
        ...(isMyFreeDtvCtgyHead && {
          padding: "0px !important"
        }),
        "& .MuiAccordionSummary-content": {
          margin: "12px 0 !important",
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          ...(isMyFreeDtvCtgyHead && {
            margin: "12px 0px 0px 0px !important",
            flexDirection: "column"
          }),
          "& .Mui-expanded": {
            minHeight: "auto" // set expanded height to same as the summary height to avoid jumping when expanded
          },
          [bps.down(bps.values.sm)]: {
            margin: "9px 0 !important",
            ...(isMyFreeDtvCtgyHead && {
              margin: "9px 0px 0px 0px !important"
            })
          }
        }
      }}
      onClick={() => handleClick(index)}>
        <DtvBox sx={{
          width: "100% !important",
          display: "flex",
          flexDirection: "row",
          ...(isMyFreeDtvCtgyHead && {
            padding: "0px 8px !important"
          })
        }}>
          <DtvBox sx={{
            display: "flex",
flexWrap: "wrap",
width: "100%"
          }}>
            {/* [WAT-12043 / YFH-AMP111] The visible category label. The
                surrounding <h2> wraps AccordionSummary above (see the
                rationale block on the CategoryHeader return statement)
                and uses aria-labelledby to point at THIS span's id, so
                the heading's accessible name is scoped to just the
                category label — not folded together with the
                AddOnPromoLegend + MyFreeInfoBlock text also rendered
                inside the summary. */}
            <Typography id={labelId} sx={{
              width: "100%",
              fontFamily: "roboto-regular",
              fontSize: "16px",
              fontWeight: 400
            }}>
              {label}
            </Typography>

            {/** Category View Add-On Info-Sec Subheader */}
            {isAddOnCtgyHead && expanded &&
              <AddOnPromoLegend
                id="addonPromo-info-container"
                addOnPromoLogoInfo={addOnPromoLogoInfo}
                textColor={theming?.textColor}
                margin={ADD_ON_LEGEND_MARGIN}
              />
            }
          </DtvBox>
          <DtvBox sx={{
            paddingRight: {
 xs: "4px",
sm: "12px",
md: "12px"
},
            alignSelf: "flex-start"
          }}>
            <CategoryExpandIcon
              accOpen={expanded}
              iconColor={theming?.textColor}
            />
          </DtvBox>
        </DtvBox>
         {/** [WAT-11256 S9] myfreeDtv subheading + logo/CTA banner — shared with the
             search-view MyFree block; see MyFreeInfoBlock.tsx */}
         {isMyFreeDtvCtgyHead && expanded && (
          <MyFreeInfoBlock
            idPrefix={id}
            myFreeConfig={myFreeConfig}
            isSatelliteChlup={isSatelliteChlup}
            subheadingTextColor={theming?.textColor}
            bannerBackground={myfreeBgColor}
          />
         )}
    </AccordionSummary>
  </h2>
  );
};

export const MemoizedCategoryHeader = React.memo(CategoryHeader);
